import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  ElementRef,
  inject,
  input,
  Renderer2,
  Signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormControlDirective, Validators } from '@angular/forms'
import { BehaviorSubject, fromEvent, map, merge, startWith, switchMap, takeUntil } from 'rxjs'

import { PaiValidators } from 'app/utils/forms/pai-validators'
import { onDestroy } from 'app/utils/rxjs/on-destroy.observable'

import { InputFieldErrorDirective } from './input-field-error.directive'

export type InputFieldTheme = 'outline' | 'fill'

@Component({
  selector: 'pai-input-field',
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'inputField',
  imports: [InputFieldErrorDirective],
})
export class InputFieldComponent implements AfterViewInit {
  private static instanceCounter = 0

  // todo: provide appearance as injection token
  readonly appearance = input<InputFieldTheme>('fill')
  // todo: provide theme as injection token
  readonly theme = input<'bright' | 'dark'>('bright')

  readonly placeholder = input<string>()
  readonly label = input.required<string>()

  readonly isActive: Signal<boolean>
  readonly showError: Signal<boolean>

  protected readonly formControlDirective = contentChild.required(FormControlDirective)
  protected readonly inputRef = contentChild.required(FormControlDirective, { read: ElementRef })

  protected inputId = computed(() => {
    return this.inputRef().nativeElement.id || `pai-input-field--${this.instanceNum}`
  })
  protected isRequired = computed((): boolean => {
    return this.control.hasValidator(Validators.required) || this.control.hasValidator(PaiValidators.requiredString)
  })
  protected darkTheme = computed(() => this.theme() === 'dark')
  protected outlineAppearance = computed(() => this.appearance() === 'outline')

  protected get control(): FormControl {
    return this.formControlDirective().control
  }

  private get inputEl(): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
    return this.inputRef().nativeElement
  }

  private readonly instanceNum = ++InputFieldComponent.instanceCounter
  private readonly isActiveSubject = new BehaviorSubject(false)
  private readonly showErrorSubject = new BehaviorSubject(false)
  private readonly renderer = inject(Renderer2)
  private readonly onDestroy$ = onDestroy()

  constructor() {
    this.isActive = toSignal(this.isActiveSubject, { requireSync: true })
    this.showError = toSignal(this.showErrorSubject.asObservable(), { requireSync: true })

    afterNextRender(() => {
      this.renderer.setAttribute(this.inputRef().nativeElement, 'id', this.inputId())
    })
  }

  // todo: get rid of ngAfterViewInit -- should no longer be necessary, use afterNextRender instead
  ngAfterViewInit(): void {
    const onFocus$ = fromEvent<void>(this.inputEl, 'focus')
    const onBlur$ = fromEvent<void>(this.inputEl, 'blur')

    merge(this.control.statusChanges, onBlur$)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.showErrorSubject.next(this.control.touched && this.control.invalid))

    setTimeout(() => {
      // set active when gaining focus
      const setActive0$ = onFocus$.pipe(map(() => true))
      // while not focused, listen to value changes and enable/disable active state
      const setActive1$ = onBlur$.pipe(
        startWith(<void>undefined),
        switchMap(() => this.control.valueChanges.pipe(takeUntil(onFocus$), startWith(this.control.value))),
        map((v) => !!v),
      )
      merge(setActive0$, setActive1$).pipe(takeUntil(this.onDestroy$)).subscribe(this.isActiveSubject)
    })
  }
}
